import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Volume2, VolumeX } from 'lucide-react';
import { AdCreative, AdEventType, recordAdEvent } from '../../api/ads/ads.api';
import { safeHref } from '../../utils/common/safe-url';
import { getDeviceId, IMPRESSION_MIN_RATIO, recordAdClick } from './ad-tracking';
import { markPreRollMissed, markPreRollShown, releasePreRollSlot } from './pre-roll-frequency';

/** Người xem bấm được "Bỏ qua quảng cáo" sau chừng này giây kể từ lúc quảng cáo bắt đầu chạy. */
export const PRE_ROLL_SKIP_AFTER_S = 5;
/** Video quảng cáo không chạy được, hoặc đứng hình quá chừng này, thì bỏ quảng cáo để video chính chạy. */
export const PRE_ROLL_STALL_MS = 3000;

export type PreRollEnd = 'skip' | 'complete' | 'abort';

interface Props {
  ad: AdCreative;
  /** Quảng cáo kết thúc. `started` = đã ghi lượt hiển thị (video đã chạy khi tab đang mở và lớp phủ trong khung nhìn). */
  onFinish: (reason: PreRollEnd, started: boolean) => void;
  /** Video quảng cáo vừa thật sự bắt đầu chạy (cùng lúc ghi lượt hiển thị). */
  onStart?: () => void;
  /** Lớp Tailwind cho vị trí hàng trên (nhãn và nút bật tiếng), để né nút của trang chứa lớp phủ. */
  topClass?: string;
}

const send = (ad: AdCreative, type: AdEventType) =>
  recordAdEvent({ campaignId: ad.campaignId, creativeId: ad.creativeId, type, deviceId: getDeviceId(), serveToken: ad.serveToken }).catch(
    () => undefined
  );

/**
 * Lớp phủ quảng cáo chèn trước video, phủ kín trình phát. Quảng cáo chạy tắt tiếng, có nút bật tiếng, nhãn "Quảng cáo", nút
 * "Tìm hiểu thêm" (click tính tiền qua hàm click dùng chung, mở link ở tab mới) và nút bỏ qua đếm ngược 5 giây.
 * Lượt hiển thị ghi đúng một lần khi video quảng cáo thật sự đang chạy, tab đang mở và lớp phủ hiện ít nhất một nửa trong khung
 * nhìn; bỏ qua ghi Skip, chạy hết ghi Complete (hai loại này máy chủ không tính tiền, chỉ gửi khi đã có lượt hiển thị). Click
 * chỉ ghi một lần mỗi quảng cáo, bấm lại vẫn mở link. Lỗi tải hoặc đứng hình quá 3 giây thì bỏ quảng cáo; chưa chạy thì không ghi gì.
 */
const PreRollAd: React.FC<Props> = ({ ad, onFinish, onStart, topClass = 'top-3' }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [muted, setMuted] = useState(true);
  const [started, setStarted] = useState(false);
  const [left, setLeft] = useState(PRE_ROLL_SKIP_AFTER_S);
  const startedRef = useRef(false);
  const impressedRef = useRef(false);
  const clickedRef = useRef(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(() => typeof IntersectionObserver === 'undefined');
  const [tabVisible, setTabVisible] = useState(() => typeof document === 'undefined' || document.visibilityState === 'visible');
  const finishedRef = useRef(false);
  const mountedRef = useRef(false);
  const stallTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onFinishRef = useRef(onFinish);
  onFinishRef.current = onFinish;

  const clearStall = () => {
    if (stallTimer.current) {
      clearTimeout(stallTimer.current);
      stallTimer.current = null;
    }
  };

  const finish = useCallback(
    (reason: PreRollEnd) => {
      if (finishedRef.current || !mountedRef.current) return;
      finishedRef.current = true;
      clearStall();
      if (reason === 'skip' && impressedRef.current) send(ad, 'Skip');
      if (reason === 'complete' && impressedRef.current) send(ad, 'Complete');
      if (!impressedRef.current) markPreRollMissed();
      try {
        videoRef.current?.pause();
      } catch {
        /* bỏ qua */
      }
      onFinishRef.current(reason, impressedRef.current);
    },
    [ad]
  );

  const armStall = useCallback(() => {
    clearStall();
    stallTimer.current = setTimeout(() => finish('abort'), PRE_ROLL_STALL_MS);
  }, [finish]);

  // Chưa chạy được trong 3 giây kể từ lúc hiện (tải chậm, trình duyệt chặn tự phát): bỏ quảng cáo.
  useEffect(() => {
    mountedRef.current = true;
    if (!startedRef.current) armStall();
    return () => {
      mountedRef.current = false; // gỡ lớp phủ rồi thì không báo gì nữa
      clearStall();
      if (!impressedRef.current && !finishedRef.current) releasePreRollSlot(); // bị gỡ giữa chừng: trả lượt hỏi
    };
  }, [armStall]);

  // Lớp phủ có trong khung nhìn không (ít nhất một nửa) và tab có đang mở không: chỉ khi đó mới tính lượt hiển thị.
  useEffect(() => {
    const el = rootRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => setInView(e.isIntersecting && e.intersectionRatio >= IMPRESSION_MIN_RATIO)),
      { threshold: [0, IMPRESSION_MIN_RATIO, 1] }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  useEffect(() => {
    const onVis = () => setTabVisible(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  // React không luôn gắn thuộc tính muted lên thẻ video: gán thẳng để trình duyệt cho tự phát.
  useEffect(() => {
    if (videoRef.current) videoRef.current.muted = muted;
  }, [muted]);

  // Đếm ngược nút bỏ qua, tính từ lúc quảng cáo bắt đầu chạy.
  useEffect(() => {
    if (!started || left <= 0) return undefined;
    const t = setTimeout(() => setLeft((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [started, left]);

  const onPlaying = () => {
    clearStall();
    if (startedRef.current) return;
    startedRef.current = true;
    setStarted(true);
  };

  // Lượt hiển thị: video đã chạy, tab đang mở, lớp phủ trong khung nhìn. Thiếu điều kiện nào thì chờ tới khi đủ.
  useEffect(() => {
    if (!started || !inView || !tabVisible || impressedRef.current || finishedRef.current) return;
    impressedRef.current = true;
    markPreRollShown();
    send(ad, 'Impression');
    onStart?.();
  }, [started, inView, tabVisible, ad, onStart]);

  const onCta = () => {
    if (clickedRef.current) {
      if (ad.landingUrl) window.open(safeHref(ad.landingUrl), '_blank', 'noopener,noreferrer');
      return;
    }
    clickedRef.current = true;
    recordAdClick(ad);
  };

  const canSkip = started && left <= 0;
  const stop = (e: React.SyntheticEvent) => e.stopPropagation();

  return (
    <div
      className="absolute inset-0 z-30 flex items-center justify-center bg-black text-white"
      ref={rootRef}
      data-testid="pre-roll-ad"
      onClick={stop}
      onDoubleClick={stop}
    >
      <video
        ref={videoRef}
        src={ad.videoUrl}
        className="h-full w-full object-contain"
        autoPlay
        muted
        playsInline
        preload="auto"
        data-testid="pre-roll-video"
        onPlaying={onPlaying}
        onTimeUpdate={clearStall}
        onWaiting={() => startedRef.current && armStall()}
        onError={() => finish('abort')}
        onEnded={() => finish('complete')}
      />

      <span className={`pointer-events-none absolute left-3 ${topClass} rounded-full bg-black/60 px-2.5 py-1 text-xs font-medium`}>
        Quảng cáo
      </span>
      <button
        type="button"
        onClick={() => setMuted((m) => !m)}
        aria-label={muted ? 'Bật tiếng quảng cáo' : 'Tắt tiếng quảng cáo'}
        className={`absolute right-3 ${topClass} flex h-11 w-11 items-center justify-center rounded-full bg-black/60 hover:bg-black/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-white`}
      >
        {muted ? <VolumeX size={20} /> : <Volume2 size={20} />}
      </button>

      <div className="absolute inset-x-3 bottom-3 flex flex-wrap items-end justify-between gap-2">
        <button
          type="button"
          onClick={onCta}
          className="min-h-[44px] max-w-full rounded-full bg-[#0071A6] px-4 text-sm font-semibold hover:brightness-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
        >
          Tìm hiểu thêm
        </button>
        <button
          type="button"
          disabled={!canSkip}
          onClick={() => finish('skip')}
          aria-live="polite"
          className="min-h-[44px] rounded-md border border-white/40 bg-black/70 px-4 text-sm font-medium enabled:hover:bg-black/90 disabled:cursor-default disabled:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
        >
          {canSkip ? 'Bỏ qua quảng cáo' : `Bỏ qua sau ${left}`}
        </button>
      </div>
    </div>
  );
};

export default PreRollAd;
